<script setup>
import { ref, onMounted } from 'vue';
import axios from 'axios';
import { useI18n } from 'vue-i18n';
import { useAuthStore } from '@stores';
import { useToast } from 'primevue/usetoast';
import { useConfirm } from 'primevue/useconfirm';
import UserForm from './components/UserForm.vue';

const apiUrl = import.meta.env.VITE_API_URL;
const { t } = useI18n({ useScope: 'global' });
const authStore = useAuthStore();
const toast = useToast();
const confirm = useConfirm();
const usersData = ref([]);
const isDialogVisible = ref(false);
const selectedUserId = ref(null);
const isEditForm = ref(false);

const getUsersData = async () => {
    try {
        const data = await axios.get(`${apiUrl}/v1/registration/users`, {
            headers: {
                Authorization: `Bearer ${authStore.userToken}`,
                'Content-Type': 'application/json',
            },
        });

        usersData.value = data.data.data;
    } catch (error) {
        console.error(error);
    }
};

const openCreateModal = () => {
    isDialogVisible.value = true;
    isEditForm.value = false;
};

const saveForm = async (data) => {
    if (!isEditForm.value) {
        const response = await axios.post(`${apiUrl}/v1/registration/user`, data, {
            headers: {
                Authorization: `Bearer ${authStore.userToken}`,
                'Content-Type': 'application/json',
            },
        });
        if (response.status == 200) {
            toast.add({ severity: 'success', summary: t('global.dataSaved'), life: 3000 });
        }
    } else {
        const response = await axios.put(`${apiUrl}/v1/registration/user`, data, {
            params: { id: selectedUserId.value },
            headers: {
                Authorization: `Bearer ${authStore.userToken}`,
                'Content-Type': 'application/json',
            },
        });
        if (response.status == 200) {
            toast.add({ severity: 'success', summary: t('form.changes_saved'), life: 3000 });
        }
    }
    getUsersData();
};

const confirmDelete = ({ id, fullName }) => {
    confirm.require({
        group: 'templating',
        message: t('userCreate.deleteMessage', { fullName }),
        header: t('userCreate.deleteTitle'),
        icon: 'pi pi-info-circle',
        rejectLabel: t('form.cancel'),
        rejectProps: {
            label: t('form.cancel'),
            severity: 'secondary',
            outlined: true,
        },
        acceptProps: {
            label: t('form.delete'),
            severity: 'danger',
        },
        accept: async () => {
            try {
                await axios.delete(`${apiUrl}/v1/registration/user`, {
                    params: {
                        id,
                    },
                    headers: {
                        Authorization: `Bearer ${authStore.userToken}`,
                        'Content-Type': 'application/json',
                    },
                });

                toast.add({ severity: 'success', summary: t('userCreate.deleteSuccess'), life: 3000 });
                getUsersData();
            } catch (error) {
                console.error(error);
            }
        },
        reject: () => {},
    });
};

const openEdit = (id) => {
    isDialogVisible.value = true;
    selectedUserId.value = id;
    isEditForm.value = true;
};

onMounted(() => {
    getUsersData();
});
</script>

<template>
    <div class="card">
        <DataTable :value="usersData" class="w-full">
            <template #empty> {{ t('global.noData') }}</template>
            <template #header>
                <div class="flex flex-wrap items-center justify-between gap-2">
                    <span class="text-xl font-bold">{{ t('userCreate.title') }}</span>
                    <Button icon="pi pi-plus" :label="t('userCreate.create')" @click="openCreateModal" />
                </div>
            </template>
            <Column field="fullName" :header="t('userCreate.fullName')"> </Column>
            <Column field="username" :header="t('userCreate.username')"> </Column>
            <Column field="role" :header="t('userCreate.role')"> </Column>

            <Column :header="t('global.actions')">
                <template #body="{ data }">
                    <div class="table-actions">
                        <Button icon="pi pi-pen-to-square" severity="info" variant="outlined" :aria-label="t('form.edit')" @click="openEdit(data.id)" />

                        <Button icon="pi pi-trash" severity="danger" variant="outlined" @click="confirmDelete(data)" />
                    </div>
                </template>
            </Column>
        </DataTable>

        <UserForm v-model:visible="isDialogVisible" v-if="isDialogVisible" @update:isVisible="isDialogVisible = $event" @save="saveForm" :userId="selectedUserId" :isEditForm="isEditForm" />
    </div>

    <ConfirmDialog group="templating" :closable="false">
        <template #message="slotProps">
            <div class="flex items-center w-full gap-4">
                <i :class="slotProps.message.icon" class="!text-4xl"></i>
                <p>{{ slotProps.message.message }}</p>
            </div>
        </template>
    </ConfirmDialog>
</template>

<style scoped>
.table-actions {
    @apply flex gap-2 items-center;
}
</style>
